import React, { useCallback, useMemo, useRef, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { animateScroll } from 'react-scroll'
import block from 'bem-cn'

import plus18Img from 'shared/img/footer/footer-18plus.png'
import logo from 'shared/img/logo.webp'

import { projectStyledName } from 'shared/constants'
import { useExitAnimation } from 'shared/hooks/useExitAnimation'
import { useOutsideClick } from 'shared/hooks/useOutsideClick'
import { localeCommonKeysType } from 'shared/locale/en-US'
import { mediaSize } from 'shared/style/var'

import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import { IHomeMenuItem } from 'components/Header/data'
import { MenuItem } from 'components/Header/MenuItem/MenuItem'
import { addNotify } from 'components/Notify'

import { footerLinks, IFooterItem, IFooterSubItem } from './data'

import './Footer.scss'

const b = block('footer')

export const Footer: React.FC = () => {
  const isMobileOrTablet = useMediaQuery({ maxWidth: mediaSize.tablet })

  const { pathname, search } = useLocation()
  const history = useHistory()
  const menuRef = useRef(null)

  const locale = useSelector(selectLocaleDict, shallowEqual).common
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)

  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null)

  const isFakeMenuOpen = useExitAnimation({ disappearDuration: 350, realIsOpen: Boolean(openMenuIndex) })

  useOutsideClick(menuRef, () => setOpenMenuIndex(null))

  const handleToggleSubMenu = (index: number | null) => {
    setOpenMenuIndex(prev => (prev === index ? null : index))
  }

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>, item: IHomeMenuItem) => {
      if (item.authRequired && !isAuth) {
        e.preventDefault()
        addNotify(locale.pleaseAuth)
      }

      if (item.mobileRoute) {
        history.push(item.mobileRoute)
        handleToggleSubMenu(null)
      } else {
        history.push(item.route)
        handleToggleSubMenu(null)
      }
    },
    [history, isAuth, locale.pleaseAuth],
  )

  const handleMenuClose = useCallback(() => {
    setOpenMenuIndex(null)
  }, [])

  const handleMenuOpen = useCallback((index: number) => {
    setOpenMenuIndex(index)
  }, [])

  const items = useMemo(
    () =>
      footerLinks.map((item, index) => (
        <MenuItem
          key={index}
          item={item}
          index={index}
          name={item.textID}
          subItems={item.subItems}
          isOpen={openMenuIndex === index}
          onOpen={handleMenuOpen}
          onClose={handleMenuClose}
        />
      )),
    [handleMenuClose, handleMenuOpen, openMenuIndex],
  )

  return (
    <footer className={b()}>
      <div className={b('content')}>
        <div className={b('top')}>
          <Link to="/" className={b('logo-link')} aria-label={locale.home} onClick={() => animateScroll.scrollToTop()}>
            <img src={logo} alt="logo" className={b('logo')} />
          </Link>

          <div className={b('license')}>
            <img src={plus18Img} alt="18+" className={b('license-icon')} />
          </div>
        </div>

        <div className={b('items')} ref={menuRef}>
          {items}
        </div>

        <div className={b('copyright')}>
          <span className={b('copyright-textID')}>{projectStyledName} ©2025. All Rights Reserved</span>
        </div>
      </div>
    </footer>
  )
}
